import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { session } from '@/lib/session';
import { stepUpLogin, passkeySupported } from '@/lib/webauthn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingMfa, setPendingMfa] = useState(null); // { csrfToken, userData }
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setLoading(true);
    setError('');

    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true }
      );
      if (data.mfa_required) {
        // Password verified, but a passkey step-up is required before any
        // protected authority is granted — do not hydrate the session yet.
        setPendingMfa({ csrfToken: data.csrf_token, userData: data });
        return;
      }
      session.hydrate(data);
      navigate('/app/overview', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail ?? 'Sign-in failed. Please try again.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleStepUp = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      await stepUpLogin(pendingMfa.csrfToken);
      session.hydrate(pendingMfa.userData);
      navigate('/app/overview', { replace: true });
    } catch (err) {
      setMfaError(err.message || 'Passkey verification failed. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  if (pendingMfa) {
    return (
      <div data-testid="mfa-stepup-page" style={{ minHeight: 'calc(100vh - 72px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 'min(100%, 420px)', padding: '48px', textAlign: 'center' }}>
          <h1 className="font-heading font-bold text-black mb-3" style={{ fontSize: '28px', letterSpacing: '-0.01em' }}>
            Verify it's you
          </h1>
          <p className="pub-body mb-8" style={{ color: '#667085' }}>
            This account requires a passkey to finish signing in.
          </p>

          {!passkeySupported() && (
            <div style={{ background: '#fee2e2', borderRadius: '3px', padding: '10px 14px', marginBottom: '16px' }} data-testid="mfa-unsupported-browser">
              <p style={{ fontSize: '13px', color: '#b91c1c' }}>
                This browser does not support passkeys. Try a recent Chrome, Safari, or Edge.
              </p>
            </div>
          )}

          {mfaError && (
            <div style={{ background: '#fee2e2', borderRadius: '3px', padding: '10px 14px', marginBottom: '16px' }} data-testid="mfa-stepup-error">
              <p style={{ fontSize: '13px', color: '#b91c1c' }}>{mfaError}</p>
            </div>
          )}

          <button
            onClick={handleStepUp}
            disabled={mfaLoading || !passkeySupported()}
            className="pub-btn-primary"
            style={{ width: '100%', justifyContent: 'center', fontSize: '15px', minHeight: '50px', opacity: mfaLoading ? 0.6 : 1 }}
            data-testid="mfa-stepup-btn"
          >
            {mfaLoading ? 'Verifying…' : 'Verify with passkey'}
          </button>

          <button
            onClick={() => { setPendingMfa(null); setPassword(''); }}
            style={{ marginTop: '16px', fontSize: '13px', color: '#667085', background: 'none', border: 'none', cursor: 'pointer' }}
            data-testid="mfa-stepup-cancel-btn"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="login-page" style={{ minHeight: 'calc(100vh - 72px)', display: 'flex', alignItems: 'stretch' }}>
      {/* Left panel */}
      <div
        style={{
          flex: '0 0 auto',
          width: 'min(100%, 520px)',
          padding: '64px 56px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: '#ffffff',
          borderRight: '1px solid #d9dee6',
        }}
        className="w-full md:w-auto"
      >
        {/* Preview marker */}
        <div className="mb-10">
          <span className="pub-preview-marker" data-testid="login-preview-marker">Private Preview</span>
        </div>

        {/* Heading */}
        <h1 className="font-heading font-bold text-black mb-3" style={{ fontSize: '36px', lineHeight: 1.08, letterSpacing: '-0.015em' }}>
          Welcome back.
        </h1>
        <p className="pub-body mb-10" style={{ color: '#667085', maxWidth: '340px' }}>
          Access your organization's governed knowledge workspace.
        </p>

        {/* Login form */}
        <form onSubmit={handleSubmit} data-testid="login-form">
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="login-email"
              className="font-heading font-semibold"
              style={{ fontSize: '11px', color: '#98a2b3', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '6px' }}
            >
              Email
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              placeholder="you@northstar-preview.local"
              style={{
                width: '100%',
                height: '44px',
                border: '1px solid #d9dee6',
                borderRadius: '3px',
                padding: '0 14px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '14px',
                color: '#101828',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              data-testid="login-email-input"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="login-password"
              className="font-heading font-semibold"
              style={{ fontSize: '11px', color: '#98a2b3', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '6px' }}
            >
              Password
            </label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              placeholder="••••••••••"
              style={{
                width: '100%',
                height: '44px',
                border: '1px solid #d9dee6',
                borderRadius: '3px',
                padding: '0 14px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '14px',
                color: '#101828',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              data-testid="login-password-input"
            />
          </div>

          {error && (
            <div
              style={{ background: '#fee2e2', borderRadius: '3px', padding: '10px 14px', marginBottom: '16px' }}
              data-testid="login-error"
            >
              <p style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '13px', color: '#b91c1c' }}>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email.trim() || !password}
            className="pub-btn-primary"
            style={{
              width: '100%',
              justifyContent: 'center',
              fontSize: '15px',
              minHeight: '50px',
              opacity: loading || !email.trim() || !password ? 0.6 : 1,
              cursor: loading || !email.trim() || !password ? 'not-allowed' : 'pointer',
            }}
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* Footer links */}
        <div className="flex gap-5 mt-10 flex-wrap">
          <Link to="/enterprise" style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '13px', color: '#667085', textDecoration: 'none' }}>
            Request enterprise access
          </Link>
          <Link to="/" style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '13px', color: '#667085', textDecoration: 'none' }}>
            Back to site
          </Link>
        </div>
      </div>

      {/* Right panel — decorative, desktop only */}
      <div
        className="hidden md:flex flex-1 items-center justify-center"
        style={{ background: '#000', padding: '64px' }}
        aria-hidden="true"
      >
        <div style={{ maxWidth: '400px', width: '100%' }}>
          <span
            className="font-wordmark text-white block leading-none mb-6"
            style={{ fontSize: '42px', opacity: 0.15 }}
          >
            KYRDROS
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              { q: 'What is authoritative?',    a: 'Approved source · active version' },
              { q: 'What changed?',              a: 'Version drift · stale guidance' },
              { q: 'What conflicts?',            a: 'ABSTAIN — human review required' },
              { q: 'Who decides?',               a: 'Named authority · recorded outcome' },
            ].map(item => (
              <div key={item.q} style={{ borderLeft: '2px solid rgba(3,243,124,0.25)', paddingLeft: '16px' }}>
                <p style={{ fontFamily: 'IBM Plex Sans, Helvetica, Arial, sans-serif', fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.65)', marginBottom: '2px' }}>
                  {item.q}
                </p>
                <p style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

